export interface ParetoFilterState {
  divisionId: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  seasonId: string | null
  planStatus: string | null
  dateFrom: string | null
  dateTo: string | null
}

export interface ParetoSkuBar {
  sku: string
  salesDollar: number
  cumulativePct: number
}

export interface ParetoTableRow {
  label: string
  level: 'division' | 'department' | 'class' | 'subclass'
  numStores: number
  salesDollar: number
  salesUnits: number
  totalProfit: number
  avgQtyPerWeek: number
  avgSalesDollarPerWeek: number
  avgSalesPerWeekStore: number
}

const BASE_BAR_BASELINE = 220000
// Default SKU pool size when no hierarchy filter is applied. Drilling down to
// a subclass narrows the SKU set the way a real catalog query would.
const BASE_BAR_COUNT = 40

// Days in the design's default date window (4/05/2026 → 11/28/2026).
const BASELINE_WINDOW_DAYS = 238

// Sibling rows at the department level — each row is one slice of the WOMENS
// division, so the absolute metrics sum cleanly into the Total footer.
const BASE_TABLE_ROWS: ParetoTableRow[] = [
  {
    label: 'DEPT-019 - Bottoms',
    level: 'department',
    numStores: 1102,
    salesDollar: 3148892,
    salesUnits: 707571,
    totalProfit: 1923159,
    avgQtyPerWeek: 22110.6,
    avgSalesDollarPerWeek: 98403,
    avgSalesPerWeekStore: 89,
  },
  {
    label: 'DEPT-023 - Tops',
    level: 'department',
    numStores: 1184,
    salesDollar: 2624076,
    salesUnits: 589632,
    totalProfit: 1602486,
    avgQtyPerWeek: 18425.7,
    avgSalesDollarPerWeek: 82002,
    avgSalesPerWeekStore: 69,
  },
  {
    label: 'DEPT-020 - Dresses',
    level: 'department',
    numStores: 902,
    salesDollar: 1574446,
    salesUnits: 353776,
    totalProfit: 961580,
    avgQtyPerWeek: 11055.5,
    avgSalesDollarPerWeek: 49201,
    avgSalesPerWeekStore: 55,
  },
  {
    label: 'DEPT-022 - Outerwear',
    level: 'department',
    numStores: 728,
    salesDollar: 1399506,
    salesUnits: 314441,
    totalProfit: 854883,
    avgQtyPerWeek: 9827.6,
    avgSalesDollarPerWeek: 43735,
    avgSalesPerWeekStore: 60,
  },
]

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function shrinkForFilter(filterValue: string, filterName: string): number {
  const seed = hashString(filterValue)
  const n = normalizedFromHash(seed, filterName)
  return 0.4 + n * 0.35
}

function skuCountForFilters(filters: ParetoFilterState): number {
  let count = BASE_BAR_COUNT
  if (filters.divisionId) count = Math.round(count * 0.75)
  if (filters.departmentId) count = Math.round(count * 0.6)
  if (filters.classId) count = Math.round(count * 0.5)
  if (filters.subclassId) count = Math.round(count * 0.45)
  return Math.max(6, count)
}

function dateRangeScale(filters: ParetoFilterState): number {
  if (!filters.dateFrom || !filters.dateTo) return 1
  const from = new Date(filters.dateFrom).getTime()
  const to = new Date(filters.dateTo).getTime()
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 1
  const days = (to - from) / 86400000
  return Math.max(0.1, days / BASELINE_WINDOW_DAYS)
}

function skuCodeForBar(seedContext: string, index: number): string {
  // Deterministic 5-digit SKU code seeded by the active filter context + bar
  // index, so re-rendering with the same filters produces the same SKU set.
  const h = hashString(`${seedContext}|sku|${index}`)
  return String(10000 + (h % 90000))
}

function activeFilterPairs(
  filters: ParetoFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.divisionId) pairs.push(['division', filters.divisionId])
  if (filters.departmentId) pairs.push(['dept', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.seasonId) pairs.push(['season', filters.seasonId])
  if (filters.planStatus) pairs.push(['status', filters.planStatus])
  // Date range is treated as a window, not a slice — it doesn't shrink scope.
  // Otherwise the default season dates would prevent the mockup from rendering
  // the screenshot baseline on first load.
  return pairs
}

function buildShrinkAndSeed(filters: ParetoFilterState) {
  const activePairs = activeFilterPairs(filters)
  const totalShrink = activePairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(value, name),
    1
  )
  const wiggleSeed = activePairs.length
    ? hashString(activePairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0
  return { totalShrink, wiggleSeed }
}

export function buildParetoBars(filters: ParetoFilterState): ParetoSkuBar[] {
  const { totalShrink, wiggleSeed } = buildShrinkAndSeed(filters)
  const barCount = skuCountForFilters(filters)
  const windowScale = dateRangeScale(filters)
  const seedContext = activeFilterPairs(filters)
    .map(([n, v]) => `${n}=${v}`)
    .join('|')

  // Pareto-shaped decay: top SKU carries most of the sales, then exponential
  // falloff so the first ~20% of SKUs cover roughly 80% of total sales.
  const rawValues: number[] = []
  let runningTotal = 0

  for (let i = 0; i < barCount; i++) {
    const decay = Math.exp(-i / (barCount / 4))
    let value = BASE_BAR_BASELINE * decay * windowScale

    if (wiggleSeed !== 0) {
      const noise = 1 + (normalizedFromHash(wiggleSeed, `bar${i}`) - 0.5) * 0.06
      value = value * totalShrink * noise
    } else {
      const jitter = 1 + (normalizedFromHash(42, `jb${i}`) - 0.5) * 0.08
      value = value * jitter
    }

    rawValues.push(value)
    runningTotal += value
  }

  const bars: ParetoSkuBar[] = []
  let cumulative = 0
  for (let i = 0; i < rawValues.length; i++) {
    cumulative += rawValues[i]
    bars.push({
      sku: skuCodeForBar(seedContext, i),
      salesDollar: rawValues[i],
      cumulativePct: cumulative / runningTotal,
    })
  }

  return bars
}

export function buildParetoTableRows(
  filters: ParetoFilterState
): ParetoTableRow[] {
  const { totalShrink, wiggleSeed } = buildShrinkAndSeed(filters)
  const windowScale = dateRangeScale(filters)

  return BASE_TABLE_ROWS.map((row, index) => {
    if (wiggleSeed === 0 && windowScale === 1) return row

    const noise = (salt: string) =>
      1 + (normalizedFromHash(wiggleSeed || 1, `${salt}${index}`) - 0.5) * 0.06
    const ratioNoise = (salt: string) =>
      1 + (normalizedFromHash(wiggleSeed || 1, `${salt}${index}`) - 0.5) * 0.1

    // Absolute metrics scale with both filter scope and the date window.
    // Ratio metrics (# of stores, per-store averages) don't shrink with the
    // time window — they're snapshot-style measures, not sums over time.
    return {
      ...row,
      numStores: Math.max(1, Math.round(row.numStores * ratioNoise('ns'))),
      salesDollar: row.salesDollar * totalShrink * windowScale * noise('sd'),
      salesUnits: row.salesUnits * totalShrink * windowScale * noise('su'),
      totalProfit: row.totalProfit * totalShrink * windowScale * noise('tp'),
      avgQtyPerWeek: row.avgQtyPerWeek * totalShrink * noise('aq'),
      avgSalesDollarPerWeek:
        row.avgSalesDollarPerWeek * totalShrink * noise('asd'),
      avgSalesPerWeekStore: row.avgSalesPerWeekStore * ratioNoise('asw'),
    }
  })
}

export function buildParetoTotalRow(rows: ParetoTableRow[]): ParetoTableRow {
  // Absolute metrics sum across sibling rows; numStores rolls up to the
  // unique-store count (max), and per-store averages are sales-weighted so
  // the footer stays internally consistent with the visible row values.
  if (rows.length === 0) {
    return {
      label: 'Total',
      level: 'division',
      numStores: 0,
      salesDollar: 0,
      salesUnits: 0,
      totalProfit: 0,
      avgQtyPerWeek: 0,
      avgSalesDollarPerWeek: 0,
      avgSalesPerWeekStore: 0,
    }
  }

  const sum = (key: keyof ParetoTableRow) =>
    rows.reduce((acc, r) => acc + (r[key] as number), 0)

  const totalSales = sum('salesDollar')
  const weightedAvgPerStore =
    totalSales > 0
      ? rows.reduce(
          (acc, r) => acc + r.avgSalesPerWeekStore * r.salesDollar,
          0
        ) / totalSales
      : 0

  return {
    label: 'Total',
    level: 'division',
    numStores: Math.max(...rows.map(r => r.numStores)),
    salesDollar: totalSales,
    salesUnits: sum('salesUnits'),
    totalProfit: sum('totalProfit'),
    avgQtyPerWeek: sum('avgQtyPerWeek'),
    avgSalesDollarPerWeek: sum('avgSalesDollarPerWeek'),
    avgSalesPerWeekStore: weightedAvgPerStore,
  }
}

export function formatCurrency0(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

export function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `$${Math.round(value / 1_000)}K`
  }
  return `$${Math.round(value)}`
}

export function formatInt(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

export function formatDecimal2(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function formatPercent0(value: number): string {
  return `${Math.round(value * 100)}%`
}
